import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
const _excluded = ["options", "values", "onChange", "entity", "entityActions"],
  _excluded2 = ["options", "values", "onChange", "entity", "entityActions"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import PropTypes from 'prop-types';
import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import ReactSelect, { components } from 'react-select';
import Creatable from 'react-select/creatable';
import { Label, FormGroup } from 'reactstrap';
import { kebabCase, isArray } from 'lodash';
import { clone } from 'ramda';
import { v4 as uuid } from 'uuid';
import classnames from 'classnames';
import validator from 'validator';
import nextTick from 'next-tick';
import _MenuList from './select/menuList';
import Option from './select/option';
import Control from './select/control';
import MiniLoader from './select/miniLoader';
import Icon from '../containers/icon';
const ClearIndicator = components.ClearIndicator,
  _DropdownIndicator = components.DropdownIndicator,
  MultiValue = components.MultiValue,
  SingleValue = components.SingleValue,
  Input = components.Input;

// Default menu stacks below fixed nav; portaled menus must clear Bootstrap modal (1050).
const SELECT_MENU_Z_INDEX = 1031;
const SELECT_MENU_PORTAL_Z_INDEX = 1060;
const Select = /*#__PURE__*/React.memo(props => {
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isEditing = _useState4[0],
    setIsEditing = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedCache = _useState6[0],
    setIsSelected = _useState6[1];
  const prepareOptions = function prepareOptions() {
    let values = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    const options = props.options,
      labelKey = props.labelKey,
      valueKey = props.valueKey,
      entity = props.entity,
      entityActions = props.entityActions;
    let opts = options || [];
    if ((!options || options.length === 0) && entity && entity.list) {
      opts = entity.list;
    }
    const knownValues = [];
    let localValues = clone(values);
    if (!isArray(localValues)) {
      localValues = [localValues];
    }

    // Get selections from the cache that weren't returned by the query
    const filteredCache = selectedCache.filter(_ref => {
      let id = _ref.id;
      return !opts.some(opt => opt.id === id);
    });
    const classNameOptions = [...opts, ...filteredCache].map(opt => {
      if (localValues.includes(opt[valueKey])) {
        knownValues.push(opt);
      }
      return _objectSpread(_objectSpread({}, opt), {}, {
        className: "t-s-opt-".concat(kebabCase(opt[labelKey]))
      });
    });
    const isValuesString = typeof values === 'string';
    // Is the "other" string a UUID?  If so, the options haven't loaded yet so don't display
    const isStringOtherValue = !isValuesString || !validator.isUUID(values);
    // Add other selections in
    if (!isArray(values) && !!values && !knownValues.length && isStringOtherValue) {
      knownValues.push(isValuesString ? {
        [labelKey]: values,
        [valueKey]: values
      } : values);
    }
    // Add any selected values to the cache
    let valuesForCache = entityActions ? knownValues.filter(_ref2 => {
      let id = _ref2.id;
      return !selectedCache.some(selected => selected.id === id);
    }) : [];
    if (valuesForCache.length) {
      valuesForCache = valuesForCache.filter(_ref3 => {
        let id = _ref3.id;
        return !knownValues.some(val => val.id === id);
      });
      setIsSelected([...valuesForCache, ...knownValues]);
    }
    return [classNameOptions, knownValues];
  };
  const alwaysOpen = props.alwaysOpen,
    allowCreate = props.allowCreate,
    incomingClassName = props.className,
    clearable = props.clearable,
    disabled = props.disabled,
    disabledText = props.disabledText,
    label = props.label,
    searchable = props.searchable,
    labelKey = props.labelKey,
    onOpen = props.onOpen,
    onTextChange = props.onTextChange,
    isParentSearching = props.isSearching,
    entity = props.entity,
    entityActions = props.entityActions,
    maxTextLength = props.maxTextLength,
    multi = props.multi,
    onChange = props.onChange,
    onFocus = props.onFocus,
    placeholder = props.placeholder,
    valueKey = props.valueKey,
    values = props.values,
    verticalPadding = props.verticalPadding,
    includeSelectionsForID = props.includeSelectionsForID,
    dropdownIndicatorGlyph = props.dropdownIndicatorGlyph,
    menuPortalTarget = props.menuPortalTarget,
    menuPosition = props.menuPosition;
  const SelectComponent = allowCreate ? Creatable : ReactSelect;
  const delayedFocus = () => {
    setIsEditing(true);
    return onFocus
    // on next tick, invoke the onFocus method.  MUST be on next tick, or
    // onFocus() does not work.
    ? nextTick(() => onFocus()) : () => {};
  };
  let placeholderText = placeholder;
  if (disabled && disabledText) {
    placeholderText = disabledText;
  }
  const className = classnames({
    't-select': true,
    't-select-single': !multi,
    't-select-multi': multi,
    't-select-other': allowCreate,
    [incomingClassName]: true,
    ["t-select-".concat(kebabCase(label))]: !!label,
    'is-disabled': disabled
  });
  const getOptionLabel = option => option[labelKey];
  const getOptionValue = option => option[valueKey];
  const getNewOptionData = (_, optionLabel) => ({
    [valueKey]: uuid(),
    [labelKey]: optionLabel,
    __isNew__: true
  });
  const _prepareOptions = prepareOptions(values),
    _prepareOptions2 = _slicedToArray(_prepareOptions, 2),
    preparedOptions = _prepareOptions2[0],
    processedValues = _prepareOptions2[1];
  const node = useRef();
  const onMenuOpen = () => {
    setIsOpen(true);
    if (onOpen) {
      onOpen();
    }
  };
  const onBlur = () => {
    setIsEditing(false);
    if (isOpen) {
      setIsOpen(false);
    }
  };
  const createOverrideWithClassNames = useCallback((Component, classNames) => function newComponent(valueProps) {
    const allProps = _objectSpread(_objectSpread({}, valueProps), {}, {
      cropWithEllipsis: false,
      className: classNames
    });
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    return /*#__PURE__*/React.createElement(Component, allProps);
  }, []);
  const createSelectOverrideWithClassNames = useCallback((Component, classNames) => valueProps => {
    const valueLabel = valueProps.data[labelKey];
    const newClassNames = "t-s-opt-".concat(kebabCase(valueLabel), " ").concat(classNames);
    return createOverrideWithClassNames(Component, newClassNames)(valueProps);
  }, [createOverrideWithClassNames, labelKey]);
  const formatCreateLabel = option => "Create \"".concat(option, "\"");
  const handleClick = event => {
    if (!node.current.contains(event.target)) {
      // outside click
      setIsOpen(false);
    }
  };
  useEffect(() => {
    if (isOpen) {
      global.document.addEventListener('mousedown', handleClick);
    } else {
      global.document.removeEventListener('mousedown', handleClick);
    }
    return () => {
      global.document.removeEventListener('mousedown', handleClick);
    };
  }, [node, isOpen]);
  let currentPageSize = 0;
  let hasNext = null;
  let isSearching = false;
  if (entityActions && entity && entity.indexQuery && entity.indexQuery.paging) {
    currentPageSize = entity.indexQuery.paging.size;
    hasNext = entity.hasNext;
    isSearching = !!entity.indexQuery.filters.text;
  }
  const pageNext = entityActions && entity ? function () {
    let nextSize = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 20;
    entityActions.serverIndex(_objectSpread(_objectSpread({}, entity.indexQuery), {}, {
      includeSelectionsForID,
      paging: {
        current: 0,
        size: currentPageSize + nextSize
      }
    }));
  } : null;
  const onSelectChange = useDebouncedCallback(text => {
    if (onTextChange) {
      onTextChange(text);
    }
    if (entityActions && entity) {
      const filters = _objectSpread({}, entity.indexQuery && entity.indexQuery.filters || {});
      if (text !== filters.text) {
        filters.text = text;
        entityActions.serverFilter(filters, _objectSpread(_objectSpread({}, entity.indexQuery), {}, {
          paging: {
            current: 0,
            size: currentPageSize
          },
          includeSelectionsForID
        }));
      }
    }
  }, 500);

  // limit the entry text to maxTextLength. The easiest way to do this is by passing a custom
  // function to onInputChange: https://github.com/JedWatson/react-select/issues/2571
  const handleInputChange = inputValue => {
    const limitedValue = inputValue.length <= maxTextLength ? inputValue : inputValue.substr(0, maxTextLength);

    // call the debounced callback for searching
    onSelectChange(limitedValue);
    return limitedValue;
  };
  const padding = processedValues && processedValues.length ? 0 : 6;
  const menuZIndex = menuPortalTarget != null ? SELECT_MENU_PORTAL_Z_INDEX : SELECT_MENU_Z_INDEX;
  const ReactSelectStyles = () => {
    const styles = {
      multiValueLabel: base => _objectSpread(_objectSpread({}, base), {}, {
        whiteSpace: 'normal',
        textOverflow: 'normal'
      }),
      input: base => _objectSpread(_objectSpread({}, base), {}, {
        margin: 0,
        paddingTop: padding,
        paddingBottom: padding
      }),
      valueContainer: base => _objectSpread(_objectSpread({}, base), {}, {
        paddingRight: 0,
        overflow: isOpen ? 'hidden' : 'unset'
      }),
      clearIndicator: base => _objectSpread(_objectSpread({}, base), {}, {
        paddingLeft: 0
      }),
      placeholder: base => _objectSpread({}, base),
      menu: base => _objectSpread(_objectSpread({}, base), {}, {
        zIndex: menuZIndex
      })
    };
    if (menuPortalTarget != null) {
      styles.menuPortal = base => _objectSpread(_objectSpread({}, base), {}, {
        zIndex: SELECT_MENU_PORTAL_Z_INDEX
      });
    }
    return styles;
  };
  useEffect(() => {
    if (entity && entityActions) {
      const query = _objectSpread(_objectSpread({}, entity.indexQuery), {}, {
        includeSelectionsForID
      });
      entityActions.serverIndex(query);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isOpen]);
  const isLoading = (entity || {}).loading || props.isLoading;
  const closeAndChange = opt => {
    if (!multi) {
      setIsOpen(false);
    }
    onChange(opt);
    if (hasNext) {
      pageNext(1);
    }
  };

  /**
   * These components will be re-created each render cycle.  We should keep an eye on
   * the react-select project for an alternative solution.  Currently, this is the reason
   * we need to keep track of focus, have special IE handling, special scroll handling
   * and special logic for controlling the opening and closing of the menu.
   */
  const selectComponents = {
    ClearIndicator: createOverrideWithClassNames(ClearIndicator, 't-select-clear'),
    DropdownIndicator: ddProps => {
      const DropdownIndic = createOverrideWithClassNames(_DropdownIndicator, 't-select-arrow-zone');
      return useMemo(() =>
      /*#__PURE__*/
      // eslint-disable-next-line react/jsx-props-no-spreading
      React.createElement(DropdownIndic, ddProps, isParentSearching ? /*#__PURE__*/React.createElement(MiniLoader, null) : /*#__PURE__*/React.createElement(Icon, {
        glyph: dropdownIndicatorGlyph
      })), [ddProps]);
    },
    /* eslint-disable-next-line react/no-unstable-nested-components */
    MenuList: menuListProps => /*#__PURE__*/React.createElement(_MenuList
    /* eslint-disable-next-line react/jsx-props-no-spreading */, _extends({}, menuListProps, {
      className: "t-select-menu",
      pageNext: pageNext,
      hasNext: hasNext,
      isLoading: isLoading,
      endMessage: props.endMessage
    })),
    Control,
    Option,
    SingleValue: createSelectOverrideWithClassNames(SingleValue, 't-select-value t-select-single-value'),
    MultiValue: createSelectOverrideWithClassNames(MultiValue, 't-select-value t-select-multi-value')
  };

  /**
   * Since IE handles input focus differently we need to defer to the
   * original Input component to prevent loss of user control.  This should be fixed
   * once react-select solves thier components problem.
   */
  const isIE = !!global.document.documentMode;
  if (!isIE) {
    /* eslint-disable-next-line react/no-unstable-nested-components */
    selectComponents.Input = inputProps =>
    /*#__PURE__*/
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    React.createElement(Input, _extends({}, inputProps, {
      autoFocus: isEditing,
      className: "t-select-input"
    }));
  }
  const localShow = isSearching && !preparedOptions.length && !isLoading;
  const overrideShow = isParentSearching === false && !preparedOptions.length && !isLoading;
  const _noOptionsMessage = () => localShow || overrideShow ? 'No results found' : null;
  const resolvedMenuPosition = menuPortalTarget != null ? 'fixed' : menuPosition;
  const select = /*#__PURE__*/React.createElement(SelectComponent, {
    key: "test",
    className: className,
    isClearable: clearable,
    getOptionLabel: getOptionLabel,
    getOptionValue: getOptionValue,
    isDisabled: disabled,
    id: label !== null ? "select-".concat(label) : 'select',
    onInputChange: handleInputChange,
    menuIsOpen: isOpen || alwaysOpen,
    onMenuOpen: onMenuOpen,
    components: selectComponents,
    isMulti: multi,
    styles: ReactSelectStyles(),
    onBlur: onBlur,
    getNewOptionData: getNewOptionData,
    formatCreateLabel: formatCreateLabel,
    onChange: closeAndChange,
    onFilter: false,
    noOptionsMessage: () => _noOptionsMessage(),
    onFocus: delayedFocus,
    options: preparedOptions,
    placeholder: placeholderText,
    isSearchable: searchable,
    value: processedValues,
    menuPortalTarget: menuPortalTarget,
    menuPosition: resolvedMenuPosition
  });
  if (verticalPadding) {
    // vertical padding is implemented with FormGroup
    return /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement("div", {
      ref: node
    }, label ? /*#__PURE__*/React.createElement(Label, null, label) : null, select));
  }
  // Form Builder controls do not want extra padding - that is controlled
  // by the format.
  return /*#__PURE__*/React.createElement("div", {
    ref: node
  }, label ? /*#__PURE__*/React.createElement(Label, null, label) : null, select);
}, (prev, next) => {
  const prevOpts = prev.options,
    prevValues = prev.values,
    onChange = prev.onChange,
    entity = prev.entity,
    entityActions = prev.entityActions,
    prevRest = _objectWithoutProperties(prev, _excluded);
  const nextOpts = next.options,
    nextValues = next.values,
    nextOnChange = next.onChange,
    nextEntity = next.entity,
    nextEntityActions = next.entityActions,
    nextRest = _objectWithoutProperties(next, _excluded2);
  const prevKeys = Object.keys(prevRest);
  const nextKeys = Object.keys(nextRest);

  // If there are a different number of props passed in, re-render
  if (prevKeys.length !== nextKeys.length) {
    return false;
  }
  const prevOptions = prevOpts || entity.list;
  const nextOptions = nextOpts || nextEntity.list;

  // Basic comparision for any other key, this won't detect function or object changes
  const propsChanged = prevKeys.some(key => prevRest[key] !== nextRest[key]);
  return !propsChanged && entity && nextEntity && entity.loading === nextEntity.loading
  // Check to see if the list has changed in size
  && prevOptions.length === nextOptions.length
  // Check to see if the user has made a new selection
  && next && next.values && prev && prev.values && next.values.length === prev.values.length;
});
Select.propTypes = {
  alwaysOpen: PropTypes.bool,
  allowCreate: PropTypes.bool,
  className: PropTypes.string,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledText: PropTypes.string,
  onTextChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isSearching: PropTypes.bool,
  entity: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({
        text: PropTypes.string
      }),
      paging: PropTypes.shape({
        size: PropTypes.number
      })
    }),
    list: PropTypes.arrayOf(PropTypes.shape({})),
    filters: PropTypes.shape({})
  }),
  entityActions: PropTypes.shape({
    serverFilter: PropTypes.func,
    serverIndex: PropTypes.func
  }),
  includeSelectionsForID: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  maxTextLength: PropTypes.number,
  multi: PropTypes.bool,
  onOpen: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  valueKey: PropTypes.string,
  endMessage: PropTypes.string,
  verticalPadding: PropTypes.bool,
  dropdownIndicatorGlyph: PropTypes.string,
  values: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])), PropTypes.oneOfType([PropTypes.string, PropTypes.object])]),
  /* eslint-disable-next-line react/forbid-prop-types -- react-select portal DOM node */
  menuPortalTarget: PropTypes.any,
  menuPosition: PropTypes.oneOf(['absolute', 'fixed'])
};
Select.defaultProps = {
  alwaysOpen: false,
  allowCreate: false,
  dropdownIndicatorGlyph: 'chevron-down',
  className: '',
  clearable: true,
  endMessage: null,
  disabled: false,
  disabledText: '',
  onTextChange: null,
  isLoading: false,
  isSearching: null,
  entity: null,
  entityActions: null,
  includeSelectionsForID: null,
  label: '',
  labelKey: 'name',
  maxTextLength: 2024,
  multi: false,
  onFocus: null,
  onOpen: null,
  options: null,
  placeholder: 'Select...',
  searchable: true,
  valueKey: 'id',
  values: [],
  verticalPadding: true,
  menuPortalTarget: null,
  menuPosition: 'absolute'
};
export default Select;